const DETAILS_KEY = 'stylon:checkout:details';
const ADDRESSES_KEY = 'stylon:checkout:savedAddresses';
const ADDRESS_TOMBSTONES_KEY = 'stylon:checkout:addressTombstones';
const INVOICE_KEY = 'stylon:checkout:invoice';

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

/** @typedef {{ name?: string, phone?: string, email?: string, permanentNote?: string, buildingNumber?: string, apartment?: string, address?: string, city?: string }} CheckoutDetails */

/** @returns {CheckoutDetails | null} */
export function loadCheckoutDetails() {
  if (typeof localStorage === 'undefined') return null;
  const parsed = safeParse(localStorage.getItem(DETAILS_KEY));
  if (!parsed || typeof parsed !== 'object') return null;
  return {
    name: String(parsed.name || '').trim() || undefined,
    phone: String(parsed.phone || '').trim() || undefined,
    email: String(parsed.email || '').trim() || undefined,
    permanentNote: String(parsed.permanentNote || '').trim() || undefined,
    buildingNumber: String(parsed.buildingNumber || '').trim() || undefined,
    apartment: String(parsed.apartment || '').trim() || undefined,
    address: String(parsed.address || '').trim() || undefined,
    city: String(parsed.city || '').trim() || undefined,
  };
}

/** @param {CheckoutDetails} details */
export function saveCheckoutDetails(details) {
  if (typeof localStorage === 'undefined') return;
  const payload = {
    name: details.name?.trim() || '',
    phone: details.phone?.trim() || '',
    email: details.email?.trim() || '',
    permanentNote: details.permanentNote?.trim() || '',
    buildingNumber: details.buildingNumber?.trim() || '',
    apartment: details.apartment?.trim() || '',
    address: details.address?.trim() || '',
    city: details.city?.trim() || '',
  };
  try {
    localStorage.setItem(DETAILS_KEY, JSON.stringify(payload));
  } catch (_) { /* ignore */ }
}

/**
 * WHAT THE SHOPPER HAS TYPED SO FAR, IN THIS TAB.
 *
 * The contact details used to live only in the checkout's state and were
 * written down (`saveCheckoutDetails`) only when the order was placed. A
 * language change remounts the whole app (App.js keys its root on the
 * language), and the checkout has no switcher of its own, so a shopper leaves
 * it to change language and comes back -- either way the name, surname and
 * phone they typed were gone while the address, saved as it is picked, stayed.
 *
 * Kept in sessionStorage: this tab, this visit -- not the device's remembered
 * details, which stay the shopper's "save my details" choice. `owner` is the
 * signed-in customer the typing happened under (null when anonymous); a draft
 * is only ever given back to the same owner, so on a shared device one
 * account's typing is never prefilled for another (CHK-2). Cleared once the
 * order is placed.
 *
 * @typedef {{ owner: string|null, name?: string, surname?: string, phone?: string, email?: string }} CheckoutDraft
 */
const DRAFT_KEY = 'stylon:checkout:draft';
const DRAFT_FIELDS = ['name', 'surname', 'phone', 'email'];

function readDraft() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const parsed = safeParse(sessionStorage.getItem(DRAFT_KEY));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

/**
 * The fields typed in this tab by `owner` (a customer id, or null when
 * anonymous) -- only the fields actually typed -- or null.
 * @returns {Partial<CheckoutDraft> | null}
 */
export function loadCheckoutDraft(owner) {
  const draft = readDraft();
  if (!draft) return null;
  if ((draft.owner ?? null) !== (owner ?? null)) return null;
  const out = {};
  DRAFT_FIELDS.forEach((field) => {
    if (typeof draft[field] === 'string') out[field] = draft[field];
  });
  return Object.keys(out).length ? out : null;
}

/** Record one typed field. A different owner's draft is replaced, never merged into. */
export function saveCheckoutDraftField(owner, field, value) {
  if (typeof sessionStorage === 'undefined' || !DRAFT_FIELDS.includes(field)) return;
  const current = readDraft();
  const sameOwner = current && (current.owner ?? null) === (owner ?? null);
  const next = { ...(sameOwner ? current : {}), owner: owner ?? null, [field]: String(value ?? '') };
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
  } catch (_) { /* storage refused */ }
}

export function clearCheckoutDraft() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch (_) { /* storage refused */ }
}

/**
 * Saved delivery addresses — localStorage cache, reconciled with the server
 * via utils/addressSync.js (`/me/addresses`) when the customer is logged in.
 * @typedef {{ id: string, label: string, labelKey?: string, address: string, city?: string, country?: string, lat?: number|null, lng?: number|null, buildingNumber?: string, apartment?: string, note?: string, lastUsedAt?: number }} SavedAddress
 */

/** @returns {SavedAddress[]} */
export function loadSavedAddresses() {
  if (typeof localStorage === 'undefined') return [];
  const parsed = safeParse(localStorage.getItem(ADDRESSES_KEY));
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter((a) => a && typeof a === 'object' && a.id && a.label && a.address)
    .map((a) => ({
      id: String(a.id),
      label: String(a.label),
      labelKey: a.labelKey ? String(a.labelKey) : '',
      address: String(a.address),
      city: a.city ? String(a.city) : '',
      country: a.country ? String(a.country) : '',
      lat: a.lat != null && Number.isFinite(Number(a.lat)) ? Number(a.lat) : null,
      lng: a.lng != null && Number.isFinite(Number(a.lng)) ? Number(a.lng) : null,
      buildingNumber: a.buildingNumber ? String(a.buildingNumber) : '',
      apartment: a.apartment ? String(a.apartment) : '',
      note: a.note ? String(a.note) : '',
      lastUsedAt: Number.isFinite(Number(a.lastUsedAt)) ? Number(a.lastUsedAt) : 0,
      // When the server last acknowledged this id (0 = never synced). This is
      // what lets a reconcile tell "the server has never seen this address, so
      // upload it" apart from "the server HAD it and it's gone now, so it was
      // deleted on another device" — without it, an address deleted on one
      // device gets re-uploaded by every other device that still has it
      // cached, and silently comes back from the dead.
      syncedAt: Number.isFinite(Number(a.syncedAt)) ? Number(a.syncedAt) : 0,
    }))
    // Most-recently-used first, so the default selection is the recent one.
    .sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

/** @param {SavedAddress[]} list */
export function saveSavedAddresses(list) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ADDRESSES_KEY, JSON.stringify(Array.isArray(list) ? list : []));
  } catch (_) { /* ignore */ }
}

/** @param {Omit<SavedAddress, 'id'> & { id?: string }} addr */
export function upsertSavedAddress(addr) {
  const list = loadSavedAddresses();
  const id = addr.id || `addr-${Date.now()}`;
  const prior = list.find((a) => a.id === id);
  const next = {
    // Carry the sync marker across an edit: the id is still one the server
    // knows, so a later reconcile must not mistake this for a never-uploaded
    // address (see `syncedAt` in loadSavedAddresses).
    syncedAt: addr.syncedAt ?? prior?.syncedAt ?? 0,
    id,
    label: String(addr.label || '').trim(),
    labelKey: String(addr.labelKey || '').trim(),
    address: String(addr.address || '').trim(),
    city: String(addr.city || '').trim(),
    country: String(addr.country || '').trim(),
    lat: addr.lat ?? null,
    lng: addr.lng ?? null,
    buildingNumber: String(addr.buildingNumber || '').trim(),
    apartment: String(addr.apartment || '').trim(),
    note: String(addr.note || '').trim(),
    lastUsedAt: Date.now(),
  };
  const idx = list.findIndex((a) => a.id === id);
  if (idx >= 0) list[idx] = next;
  else list.push(next);
  saveSavedAddresses(list);
  return next;
}

export function removeSavedAddress(id) {
  saveSavedAddresses(loadSavedAddresses().filter((a) => a.id !== id));
  // Remember the deletion until the server confirms it. The delete request is
  // fire-and-forget (it can fail offline, or the customer may not be logged
  // in yet), and the next reconcile pulls the server's list — without a
  // tombstone that pull would happily re-add the address we just deleted.
  addAddressTombstone(id);
}

/**
 * Ids deleted locally whose deletion the server hasn't confirmed yet.
 * Cleared by the reconcile in utils/addressSync.js once the server's list no
 * longer contains them.
 */
export function loadAddressTombstones() {
  if (typeof localStorage === 'undefined') return [];
  const parsed = safeParse(localStorage.getItem(ADDRESS_TOMBSTONES_KEY));
  return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
}

export function saveAddressTombstones(ids) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ADDRESS_TOMBSTONES_KEY, JSON.stringify(Array.from(new Set(ids || []))));
  } catch (_) { /* ignore */ }
}

export function addAddressTombstone(id) {
  if (!id) return;
  saveAddressTombstones([...loadAddressTombstones(), String(id)]);
}

/** Mark a saved address as just-used so it becomes the default next time. */
export function touchSavedAddress(id) {
  const list = loadSavedAddresses();
  const idx = list.findIndex((a) => a.id === id);
  if (idx < 0) return;
  list[idx] = { ...list[idx], lastUsedAt: Date.now() };
  saveSavedAddresses(list);
}

/**
 * Saved invoice details — reused on future orders (like saved addresses).
 * @typedef {{ invoiceType?: string, nationalId?: string, companyName?: string, vatNumber?: string, billing?: object }} InvoiceDetails
 */

/** @returns {InvoiceDetails | null} */
export function loadInvoiceDetails() {
  if (typeof localStorage === 'undefined') return null;
  const parsed = safeParse(localStorage.getItem(INVOICE_KEY));
  if (!parsed || typeof parsed !== 'object') return null;
  return {
    invoiceType: parsed.invoiceType === 'corporate' ? 'corporate' : 'individual',
    nationalId: String(parsed.nationalId || '').trim(),
    companyName: String(parsed.companyName || '').trim(),
    vatNumber: String(parsed.vatNumber || '').trim(),
    billing: (parsed.billing && typeof parsed.billing === 'object') ? {
      name: String(parsed.billing.name || ''),
      surname: String(parsed.billing.surname || ''),
      country: String(parsed.billing.country || ''),
      province: String(parsed.billing.province || ''),
      district: String(parsed.billing.district || ''),
      postalCode: String(parsed.billing.postalCode || ''),
      address: String(parsed.billing.address || ''),
    } : null,
  };
}

/** @param {InvoiceDetails} details */
export function saveInvoiceDetails(details) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(INVOICE_KEY, JSON.stringify(details || {}));
  } catch (_) { /* ignore */ }
}

/** Resolve customer display fields from auth + API profile. */
export function resolveCustomerFields(currentUser, meProfile, savedDetails) {
  const fromAuth = currentUser || {};
  const fromMe = meProfile || {};
  const saved = savedDetails || {};
  const name = (
    saved.name
    || fromMe.fullName
    || fromMe.displayName
    || fromAuth.fullName
    || fromAuth.displayName
    || fromAuth.name
    || ''
  ).trim();
  const phone = (saved.phone || fromMe.phone || fromAuth.phone || '').trim();
  const email = (saved.email || fromMe.email || fromAuth.email || '').trim();
  return { name, phone, email };
}
